import React from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { serviceAreas } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';

const AreasSection = () => {
  return (
    <section id="areas" className="section">
      <div className="container">
        {/* Section Header */}
        <ScrollReveal animation="fadeUp">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span 
              className="inline-block text-sm font-semibold uppercase tracking-wider mb-4 px-4 py-1.5 rounded-full"
              style={{ 
                color: 'var(--accent-blue)',
                background: 'var(--accent-blue-light)'
              }}
            >
              Service Areas
            </span>
            <h2 className="heading-1 mb-5">Proudly Serving San Diego</h2>
            <p className="body-large">
              We bring our professional cleaning services to neighborhoods across 
              the San Diego area.
            </p>
          </div>
        </ScrollReveal>

        {/* Areas Grid */}
        <ScrollReveal animation="fadeUp" delay={200}>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto mb-12">
            {serviceAreas.map((area, index) => {
              const colors = [
                { bg: 'var(--accent-blue-light)', icon: 'var(--accent-blue)', border: 'rgba(74, 144, 184, 0.3)' },
                { bg: 'var(--accent-green-light)', icon: 'var(--accent-green)', border: 'rgba(123, 168, 142, 0.3)' },
                { bg: 'var(--accent-gold-light)', icon: 'var(--accent-gold)', border: 'rgba(201, 169, 98, 0.3)' }
              ];
              const color = colors[index % 3];

              return (
                <div 
                  key={area.name}
                  className={`group flex items-center gap-2.5 px-5 py-3 rounded-full transition-all duration-300 cursor-default ${
                    area.important 
                      ? 'shadow-sm border-2' 
                      : 'hover:shadow-md'
                  }`}
                  style={{ 
                    background: color.bg,
                    borderColor: area.important ? color.icon : color.border,
                    borderStyle: 'solid',
                    borderWidth: area.important ? '2px' : '1px'
                  }}
                >
                  <MapPin 
                    size={16} 
                    style={{ color: color.icon }}
                    className="transition-transform duration-300 group-hover:scale-110"
                  />
                  <span 
                    className={`text-sm ${area.important ? 'font-bold' : 'font-medium'}`}
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {area.name}
                  </span>
                </div>
              );
            })}
          </div>
        </ScrollReveal>

        {/* CTA Card */}
        <ScrollReveal animation="fadeUp" delay={300}>
          <div 
            className="relative overflow-hidden p-8 md:p-10 rounded-3xl max-w-3xl mx-auto"
            style={{ 
              background: 'linear-gradient(135deg, var(--navy-primary) 0%, var(--navy-light) 100%)',
              boxShadow: '0 20px 50px rgba(26, 62, 92, 0.25)'
            }}
          >
            {/* Background decoration */}
            <div 
              className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
              style={{ 
                background: 'radial-gradient(circle, white 0%, transparent 70%)',
                transform: 'translate(30%, -30%)'
              }}
            />

            <div className="relative z-10 text-center">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                <Navigation size={24} color="white" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">
                Don't See Your Neighborhood?
              </h3>
              <p className="text-white/80 mb-6 max-w-md mx-auto">
                We're continuously expanding our service area. Contact us to check 
                availability in your location.
              </p>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{ 
                  background: 'white',
                  color: 'var(--navy-primary)'
                }}
              >
                Check Availability
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default AreasSection;
